import {
  INodeMapping,
  IParsedVersionMapping,
  IVersionMappingUpdate,
} from '../tree/TreeActions'
import { INode } from 'shared/types/tree'
import * as React from 'react'
import { Box } from '@mui/material'
import { ToggleButton, ToggleButtonGroup } from '@mui/lab'
import { MatchedMappings } from './MatchedMappings'
import { ManualMapping } from './ManualMapping'
import { AutoMapping } from './AutoMapping'
import { MappingDuplicates } from './MappingIssues'
import { filteredMappings, MappingFilterType, NodeIdMap } from './utility'
import { ConfirmCommitButton } from 'shared/ui'
import { doInBatches } from '../../utility'

export function MappingManager({
  mapping,
  destination,
  source,
  sourceIdMapping,
  destinationMapping,
  reviewFilters,
  reviewIndex,
  doUpdate,
  updateMappingFunc,
}: {
  mapping: IParsedVersionMapping
  sourceIdMapping: NodeIdMap
  destinationMapping: NodeIdMap
  destination: INode
  source: INode
  reviewFilters?: MappingFilterType[]
  reviewIndex?: number
  doUpdate: () => void
  updateMappingFunc: (
    updateInfo: IVersionMappingUpdate,
    skipUpdate?: boolean,
  ) => Promise<void>
}) {
  const [selectedAction, setSelectedAction] = React.useState(
    reviewFilters?.length ? 'review' : '',
  )
  const [mismatchedPaths, setMismatchedPaths] = React.useState<INodeMapping[]>(
    [],
  )

  React.useEffect(() => {
    setMismatchedPaths(
      filteredMappings(
        mapping.matched,
        sourceIdMapping,
        destinationMapping,
        MappingFilterType.MismatchedPaths,
      ),
    )
  }, [mapping, sourceIdMapping, destinationMapping])

  function onActionSelect(action: string) {
    if (selectedAction === action) {
      setSelectedAction('')
    } else {
      setSelectedAction(action)
    }
  }

  async function deleteAllUnmapped() {
    const deleteMapping = (mapping: INodeMapping) => {
      return updateMappingFunc(
        { id: mapping.id, deleted: true, to_node_id: '' },
        true,
      )
    }
    await doInBatches(mapping.missing, deleteMapping)
    doUpdate()
  }

  return (
    <Box>
      <Box>
        <ConfirmCommitButton onClick={deleteAllUnmapped}>
          Delete All Unresolved
        </ConfirmCommitButton>
      </Box>
      <ToggleButtonGroup
        value={selectedAction}
        exclusive={true}
        onChange={(e, v) => onActionSelect(v)}
      >
        <ToggleButton value="map">Start Mapping</ToggleButton>
        <ToggleButton value="review">Review Mapped</ToggleButton>
        <ToggleButton value="auto">Auto All</ToggleButton>
        <ToggleButton value="issues">Issues</ToggleButton>
      </ToggleButtonGroup>
      {selectedAction === 'map' && (
        <ManualMapping
          missingMapping={mapping.missing}
          sourceIdMapping={sourceIdMapping}
          destinationMapping={destinationMapping}
          updateMappingFunc={updateMappingFunc}
          destination={destination}
          sourceTree={source}
        />
      )}
      {selectedAction === 'review' && (
        <MatchedMappings
          matched={mapping.matched}
          sourceIdMapping={sourceIdMapping!}
          destIdMapping={destinationMapping!}
          destTree={destination}
          sourceTree={source}
          updateMappingFunc={updateMappingFunc}
          reviewFilters={reviewFilters}
          reviewIndex={reviewIndex}
        />
      )}
      {selectedAction === 'auto' && (
        <AutoMapping
          missingMapping={mapping.missing}
          mismatchedPaths={mismatchedPaths}
          sourceIdMapping={sourceIdMapping}
          destinationMapping={destinationMapping}
          destination={destination}
          source={source}
          onUpdate={doUpdate}
          updateMappingFunc={updateMappingFunc}
        />
      )}
      {selectedAction === 'issues' && (
        <MappingDuplicates
          mapping={mapping}
          sourceIdMapping={sourceIdMapping}
          destinationMapping={destinationMapping}
        />
      )}
    </Box>
  )
}
